import { MONTANA_CONSTANTS } from './montanaConstants';

export interface PricingCalculation {
  parcels: number;
  silver: {
    monthly: number;
    annual: number;
    setup: number;
  };
  gold: {
    monthly: number;
    annual: number;
    setup: number;
  };
  // Note: Custom pricing is handled separately via quotes
}

export const calculatePricing = (parcels: number): PricingCalculation => {
  return {
    parcels,
    silver: {
      monthly: parcels * MONTANA_CONSTANTS.PRICING.SILVER,
      annual: parcels * MONTANA_CONSTANTS.PRICING.SILVER * 12,
      setup: Math.round(parcels * MONTANA_CONSTANTS.SETUP_RATES_PER_PARCEL.SILVER),
    },
    gold: {
      monthly: parcels * MONTANA_CONSTANTS.PRICING.GOLD,
      annual: parcels * MONTANA_CONSTANTS.PRICING.GOLD * 12,
      setup: Math.round(parcels * MONTANA_CONSTANTS.SETUP_RATES_PER_PARCEL.GOLD),
    },
  };
};

export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

export const formatNumber = (num: number): string => {
  return new Intl.NumberFormat('en-US').format(num);
};

export const calculateROI = (
  currentCosts: number,
  newCosts: number,
): {
  savings: number;
  savingsPercentage: number;
  paybackMonths: number;
} => {
  const savings = currentCosts - newCosts;
  const savingsPercentage = (savings / currentCosts) * 100;
  const paybackMonths = newCosts / (savings / 12);

  return {
    savings,
    savingsPercentage,
    paybackMonths,
  };
};

export const calculateAnalogToDigitalCost = (
  hours: number,
  complexity: 'organized' | 'moderate' | 'disorganized',
): number => {
  const rates = MONTANA_CONSTANTS.ANALOG_TO_DIGITAL.HOURLY_RATES;
  const hourlyRate = rates[complexity.toUpperCase() as keyof typeof rates];
  return hours * hourlyRate;
};

export const getMostPopularTier = (): 'silver' | 'gold' | 'custom' => {
  return 'gold'; // Gold is now the most popular (formerly Silver)
};

export const getRecommendedTier = (parcels: number): 'silver' | 'gold' | 'custom' => {
  if (parcels < 300) return 'silver';
  if (parcels < 600) return 'gold';
  return 'custom';
};

export const validateParcelCount = (parcels: number): boolean => {
  return parcels > 0 && parcels <= 10000; // Reasonable limits
};

export const generateShareableLink = (parcels: number, tier: string): string => {
  const baseUrl = window.location.origin;
  return `${baseUrl}/pricing/montana?parcels=${parcels}&tier=${tier}`;
};

export const scrollToElement = (elementId: string): void => {
  const element = document.getElementById(elementId);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' });
  }
};
